'use client';

import { useTranslation } from 'react-i18next';
import {
  useMicroSource,
  useMicroModuleConfig,
  resolveMicroListingChrome,
  useBlockPart,
  MICRO_KIND_BY_SLUG,
  isKnownMicroKindSlug,
  PortalLink,
  type MicroContentItem,
  type MicroContentNamespace,
  type MicroCategorySummary,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { MicroItemCard } from '../micro/_MicroItemCard';
import { microCardGridStyle } from '../micro/_cardGrid';
import { useMicroCardDecorations } from '../micro/_useMicroCardDecorations';
import { EmptyState } from '../_shared/EmptyState';
import { BlockRoot } from '../_shared/BlockRoot';
import { sectionPad, sectionInner } from '../_shared/styles';

/**
 * /micro/[kindSlug] — paginated listing for one micro-content kind.
 * Renders a 2-level breadcrumb, count subtitle, search box, category
 * filter, the result grid and a windowed numeric pager. The kind is
 * resolved from the slug via {@link MICRO_KIND_BY_SLUG}.
 *
 * <p>The search box and the category filter follow the tenant's micro
 * module config ({@link resolveMicroListingChrome}): the box renders for
 * placement {@code body} / {@code both} (the header placements are the
 * shell's business), and categories sit in a pill rail above the grid,
 * in a sidebar beside it, or nowhere. The reserved {@code search} slug
 * always keeps its box — it IS the search page.
 *
 * <p>The reserved {@code search} slug renders the all-kinds listing —
 * it's where the discovery row's search box submits {@code ?q=}
 * (legacy's /micro/search page).
 */
export interface MicroListingLayoutProps {
  kindSlug: string;
  page?: number;
  pageSize?: number;
  category?: string | number;
  q?: string;
  /** Topic-tag filter (from {@code ?tag=}) — what a tag chip in the
   *  short-video player links to. */
  tag?: string;
  itemHrefTemplate?: string;
  categoryHrefTemplate?: string;
  /** Breadcrumb root — the micro landing. */
  indexHref?: string;
}

/** Reserved pseudo-slug for the cross-kind search listing. */
const SEARCH_SLUG = 'search';

/** One wide page for a tag-filtered listing — tags are sparse, so the
 *  set fits; the client narrows it (see the fetch). */
const TAG_FILTER_PAGE_SIZE = 200;

type ListResult = Awaited<ReturnType<MicroContentNamespace['list']>>;

function narrowByTag(list: ListResult, tag: string): ListResult {
  const wanted = tag.trim().toLowerCase();
  const items = list.items.filter((it) =>
    (it.topicTags ?? []).some((t) => t.toLowerCase() === wanted),
  );
  return {
    ...list,
    items,
    pagination: { page: 1, pageSize: items.length || 1, pageCount: 1, total: items.length },
  };
}

export function MicroListingLayout({
  kindSlug,
  page = 1,
  pageSize = 12,
  category,
  q,
  tag,
  itemHrefTemplate,
  categoryHrefTemplate = `?category={slug}`,
  indexHref = '/lxp/micro-learning',
}: MicroListingLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const micro = useMicroSource();
  const chrome = resolveMicroListingChrome(useMicroModuleConfig() ?? undefined);
  // Pills need the full category list anyway, so the hook skips its own
  // categories fetch and card names resolve from this layout's read.
  const decorations = useMicroCardDecorations({ categories: false });
  const isSearch = kindSlug === SEARCH_SLUG;
  const kind =
    !isSearch && isKnownMicroKindSlug(kindSlug) ? MICRO_KIND_BY_SLUG[kindSlug] : undefined;
  const unknownSlug = kindSlug !== '' && !isSearch && kind === undefined;

  const state = useAsyncResource(
    async (signal) => {
      if (!micro) return null;
      const [raw, categories, types] = await Promise.all([
        micro.content.list({
          ...(kind ? { kind } : {}),
          ...(category !== undefined ? { category } : {}),
          ...(q ? { q } : {}),
          ...(tag ? { tag } : {}),
          // Tag filtering is enforced here as well as upstream (below), so
          // pull one wide page and let the client do the narrowing.
          page: tag ? 1 : page,
          pageSize: tag ? TAG_FILTER_PAGE_SIZE : pageSize,
          signal,
        }),
        micro.categories.list({ signal }),
        micro.types.list({ signal }),
      ]);
      // The backend does not (yet) honour `tag`; until it does, a tag
      // chip must still land on a list that only contains that tag, so
      // the items are narrowed by topicTags here. Harmless once upstream
      // filters too — every item already matches.
      const list = tag ? narrowByTag(raw, tag) : raw;
      return { list, categories, types };
    },
    [micro, kind, category, q, tag, page, pageSize],
  );

  if (!micro) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="MicroListingLayout" adapterName="MicroSource" />
        </div>
      </BlockRoot>
    );
  }
  if (unknownSlug) {
    // An unrecognised kind slug must not fall through to an all-kinds
    // listing wearing the wrong title (e.g. a GAME tile before the games
    // phase ships its player).
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            title={t('microListing.unavailable.title')}
            hint={t('microListing.unavailable.hint')}
          />
        </div>
      </BlockRoot>
    );
  }
  if (state.kind !== 'loaded') return null;
  const { list, categories, types } = state.data;
  const typeMeta = isSearch ? undefined : types.find((t) => t.kindSlug === kindSlug);
  const heading = isSearch ? t('microListing.searchHeading') : (typeMeta?.displayName ?? kindSlug);

  const activeCategory =
    category !== undefined
      ? (categories.find((c) => String(c.id) === String(category)) ?? null)
      : null;
  const countLabel = t('microListing.itemCount', { count: list.pagination.total });
  const subtitle = activeCategory
    ? `${activeCategory.name} · ${countLabel}`
    : list.pagination.total > 0
      ? countLabel
      : undefined;

  const showSearch = chrome.showBodySearch || isSearch;
  const filter = { categories, activeId: category, hrefTemplate: categoryHrefTemplate, q, tag };
  const sidebar = chrome.categoriesPlacement === 'sidebar' && categories.length > 0;

  const results = (
    <>
      {list.items.length > 0 ? (
        <div style={microCardGridStyle(kind)}>
          {list.items.map((item: MicroContentItem) => (
            <MicroItemCard
              key={item.id}
              cover={kind === 'SHORTS' ? 'portrait' : 'landscape'}
              item={item}
              progress={decorations.progressFor(item.id)}
              categoryName={
                item.categoryId != null
                  ? (categories.find((c) => String(c.id) === String(item.categoryId))?.name ?? null)
                  : null
              }
              {...(itemHrefTemplate ? { hrefTemplate: itemHrefTemplate } : {})}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title={t('microListing.empty.title')}
          hint={
            q || tag ? t('microListing.empty.searchHint') : t('microListing.empty.categoryHint')
          }
        />
      )}
      {list.pagination.pageCount > 1 && (
        <Pager
          page={list.pagination.page}
          pageCount={list.pagination.pageCount}
          category={category}
          q={q}
          tag={tag}
        />
      )}
    </>
  );

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        <Breadcrumb
          indexHref={indexHref}
          kindName={heading}
          activeCategoryName={activeCategory?.name ?? null}
          q={q}
          tag={tag}
        />
        <h1 style={{ margin: 0, fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700 }}>
          {heading}
        </h1>
        {subtitle && (
          <p
            style={{ marginTop: 4, marginBottom: 0, color: 'var(--color-text-secondary, #71717a)' }}
          >
            {subtitle}
          </p>
        )}
        {typeMeta?.description && (
          <p style={{ marginTop: 4, color: 'var(--color-text-secondary, #71717a)' }}>
            {typeMeta.description}
          </p>
        )}
        {(tag || activeCategory) && (
          <ActiveFilterStrip
            tag={tag}
            categoryName={activeCategory?.name ?? null}
            total={list.pagination.total}
            q={q}
          />
        )}
        {showSearch && (
          <SearchBox
            category={category}
            tag={tag}
            initialQ={q ?? ''}
            placeholder={chrome.searchPlaceholder}
          />
        )}
        {chrome.categoriesPlacement === 'rail-top' && categories.length > 0 && (
          <CategoryPills {...filter} />
        )}
        {sidebar ? (
          // Sidebar beside the grid on wide screens; the flex bases make it
          // wrap above the grid once the two can't share the row.
          <div
            style={{
              marginTop: 24,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 32,
              alignItems: 'flex-start',
            }}
          >
            <CategorySidebar {...filter} />
            <div style={{ flex: '999 1 480px', minWidth: 0 }}>{results}</div>
          </div>
        ) : (
          <div style={{ marginTop: 24 }}>{results}</div>
        )}
      </div>
    </BlockRoot>
  );
}

/**
 * Two-level breadcrumb above the title — small uppercase trail:
 * {@code Mikro Öğrenme › <kind>}, plus a third non-link segment when a
 * category filter is active (you're already there).
 */
function Breadcrumb({
  indexHref,
  kindName,
  activeCategoryName,
  q,
  tag,
}: {
  indexHref: string;
  kindName: string;
  activeCategoryName: string | null;
  q: string | undefined;
  tag: string | undefined;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const sep = (
    <span
      aria-hidden="true"
      style={{ margin: '0 6px', color: 'var(--color-text-tertiary, #94a3b8)' }}
    >
      ›
    </span>
  );
  const current = { color: 'var(--color-text-primary, #18181b)' };
  return (
    <nav
      aria-label={t('shared.nav.breadcrumb')}
      style={{
        marginBottom: 12,
        fontSize: '11px',
        letterSpacing: 'var(--font-tracking-widest, 0.08em)',
        textTransform: 'uppercase',
        color: 'var(--color-text-secondary, #94a3b8)',
      }}
    >
      <PortalLink href={indexHref} style={{ color: 'inherit', textDecoration: 'none' }}>
        {t('microListing.breadcrumb.root')}
      </PortalLink>
      {sep}
      {activeCategoryName ? (
        <>
          {/* Clearing the category keeps an active search. */}
          <PortalLink
            href={withQuery('?', q, tag)}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {kindName}
          </PortalLink>
          {sep}
          <span style={current}>{activeCategoryName}</span>
        </>
      ) : (
        <span style={current}>{kindName}</span>
      )}
    </nav>
  );
}

/**
 * Search input — plain GET form to the current listing path so SSR
 * navigation stays canonical (no client JS needed). The active
 * category rides along as a hidden field; submitting resets ?page.
 */
function SearchBox({
  category,
  tag,
  initialQ,
  placeholder,
}: {
  category: string | number | undefined;
  tag: string | undefined;
  initialQ: string;
  /** Operator copy from the module config; falls back to the catalogue. */
  placeholder: string | undefined;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <form
      method="get"
      role="search"
      style={{
        display: 'flex',
        gap: 8,
        marginTop: 16,
        alignItems: 'stretch',
        maxWidth: 520,
      }}
    >
      {category !== undefined && category !== '' && (
        <input type="hidden" name="category" value={String(category)} />
      )}
      {tag && <input type="hidden" name="tag" value={tag} />}
      <input
        {...part('search')}
        type="search"
        name="q"
        defaultValue={initialQ}
        placeholder={placeholder ?? t('microListing.search.placeholder')}
        aria-label={t('microListing.search.label')}
        style={{
          flex: 1,
          padding: 'var(--space-2, 8px) var(--space-3, 12px)',
          font: 'inherit',
          fontSize: 'var(--font-size-sm, 14px)',
          borderRadius: 'var(--radius-md, 8px)',
          border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
          background: 'var(--color-surface-card, #fff)',
          color: 'var(--color-text-primary, #18181b)',
          outline: 'none',
          minWidth: 0,
        }}
      />
      <button
        {...part('submit')}
        type="submit"
        style={{
          padding: 'var(--space-2, 8px) var(--space-4, 16px)',
          font: 'inherit',
          fontSize: 'var(--font-size-sm, 14px)',
          fontWeight: 600,
          borderRadius: 'var(--radius-md, 8px)',
          border: 'none',
          background: 'var(--color-brand-500, #3b5bdb)',
          color: 'var(--color-text-on-brand, #fff)',
          cursor: 'pointer',
        }}
      >
        {t('microListing.search.submit')}
      </button>
      {initialQ && (
        <PortalLink
          href={withTag(
            category !== undefined && category !== ''
              ? `?category=${encodeURIComponent(String(category))}`
              : '?',
            tag,
          )}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0 12px',
            fontSize: '13px',
            color: 'var(--color-text-secondary, #94a3b8)',
            textDecoration: 'none',
          }}
        >
          {t('microListing.search.clear')}
        </PortalLink>
      )}
    </form>
  );
}

/**
 * Windowed numeric pager — first, last and 2 pages either side of the
 * current one, gaps collapsed to an ellipsis; plain ?page= anchors so
 * SSR stays canonical. Active filter state rides along or paging would
 * silently drop it.
 */
function Pager({
  page,
  pageCount,
  category,
  q,
  tag,
}: {
  page: number;
  pageCount: number;
  category?: string | number;
  q?: string;
  tag?: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const href = (target: number) => {
    const params = new URLSearchParams({ page: String(target) });
    if (category !== undefined && category !== '') params.set('category', String(category));
    if (q) params.set('q', q);
    if (tag) params.set('tag', tag);
    return `?${params.toString()}`;
  };
  const arrow = (target: number, label: string, enabled: boolean) =>
    enabled ? (
      <PortalLink href={href(target)} style={pageLink(false)}>
        {label}
      </PortalLink>
    ) : (
      <span style={{ ...pageLink(false), opacity: 0.4 }}>{label}</span>
    );
  return (
    <nav
      {...part('pager')}
      aria-label={t('shared.nav.pagination')}
      style={{
        marginTop: 32,
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}
    >
      {arrow(page - 1, t('shared.nav.prevArrow'), page > 1)}
      {computePageWindow(page, pageCount).map((p, i) =>
        p === '…' ? (
          <span
            key={`gap-${i}`}
            style={{ padding: '4px 6px', color: 'var(--color-text-secondary, #94a3b8)' }}
          >
            …
          </span>
        ) : p === page ? (
          <span key={p} style={pageLink(true)} aria-current="page">
            {p}
          </span>
        ) : (
          <PortalLink key={p} href={href(p)} style={pageLink(false)}>
            {p}
          </PortalLink>
        ),
      )}
      {arrow(page + 1, t('shared.nav.nextArrow'), page < pageCount)}
    </nav>
  );
}

/**
 * Page numbers to show around {@code current}, with {@code '…'} for a
 * collapsed range. Always includes 1 and {@code total}; always includes
 * the 2 pages on each side of current.
 *
 * <pre>
 *   computePageWindow(1, 5)   → [1, 2, 3, 4, 5]
 *   computePageWindow(8, 50)  → [1, '…', 6, 7, 8, 9, 10, '…', 50]
 *   computePageWindow(50, 50) → [1, '…', 48, 49, 50]
 * </pre>
 */
export function computePageWindow(current: number, total: number): (number | '…')[] {
  if (total <= 1) return [1];
  const windowSize = 2; // pages either side of current
  const pages = new Set<number>([1, total, current]);
  for (let d = 1; d <= windowSize; d++) {
    if (current - d >= 1) pages.add(current - d);
    if (current + d <= total) pages.add(current + d);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]!);
    const next = sorted[i + 1];
    if (next !== undefined && next - sorted[i]! > 1) out.push('…');
  }
  return out;
}

function pageLink(active: boolean) {
  return {
    padding: '5px 10px',
    minWidth: 32,
    textAlign: 'center' as const,
    borderRadius: 'var(--radius-sm, 6px)',
    fontSize: 'var(--font-size-xs, 12px)',
    fontWeight: active ? 700 : 500,
    background: active ? 'var(--color-brand-500, #3b5bdb)' : 'transparent',
    color: active ? '#fff' : 'var(--color-text-primary, #18181b)',
    border: '1px solid ' + (active ? 'transparent' : 'var(--color-border, rgba(0,0,0,0.12))'),
    textDecoration: 'none',
    display: 'inline-block',
  };
}

interface CategoryFilterProps {
  categories: MicroCategorySummary[];
  activeId?: string | number;
  hrefTemplate: string;
  q?: string;
  tag?: string;
}

/** Append one query pair, minding whether the href already has a query. */
function appendParam(href: string, key: string, value: string | undefined): string {
  if (!value) return href;
  const sep = href.includes('?') ? (href.endsWith('?') ? '' : '&') : '?';
  return `${href}${sep}${key}=${encodeURIComponent(value)}`;
}

/** Switching category resets the page but must keep an active search
 *  and tag filter. */
function withQuery(href: string, q: string | undefined, tag?: string): string {
  return appendParam(appendParam(href, 'q', q), 'tag', tag);
}

function withTag(href: string, tag: string | undefined): string {
  return appendParam(href, 'tag', tag);
}

/**
 * The active filter, spelled out above the results — a learner who
 * arrived from a tag or category chip in the player must see which
 * filter is on and be able to drop it to get back to every item of the
 * kind. Clearing keeps an active search.
 */
function ActiveFilterStrip({
  tag,
  categoryName,
  total,
  q,
}: {
  tag: string | undefined;
  categoryName: string | null;
  total: number;
  q: string | undefined;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const parts: Array<{ label: string; value: string }> = [];
  if (categoryName) parts.push({ label: t('microListing.filter.category'), value: categoryName });
  if (tag) parts.push({ label: t('microListing.filter.tagLabel'), value: tag });
  return (
    <div
      {...part('activeFilter')}
      style={{
        marginTop: 18,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px 14px',
        padding: '10px 12px 10px 14px',
        background: 'var(--color-surface-card, #fff)',
        border: '1px solid var(--color-border, #e4e4e7)',
        borderRadius: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--color-brand-500, #0b7cb2)',
          fontSize: '11px',
          fontWeight: 800,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {t('microListing.filter.active')}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 6,
          fontSize: 'var(--font-size-sm, 14px)',
        }}
      >
        {parts.map((p, i) => (
          <span key={p.label} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {i > 0 && <span style={{ color: 'var(--color-text-tertiary, #a1a1aa)' }}>·</span>}
            <span style={{ color: 'var(--color-text-secondary, #71717a)' }}>{p.label}:</span>
            <span style={{ fontWeight: 700 }}>{p.value}</span>
          </span>
        ))}
        <span style={{ color: 'var(--color-text-tertiary, #a1a1aa)' }}>·</span>
        <span style={{ color: 'var(--color-text-secondary, #52525b)' }}>
          {t('microListing.itemCount', { count: total })}
        </span>
      </div>
      <PortalLink
        href={appendParam('?', 'q', q)}
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 12px',
          borderRadius: 8,
          border: '1px solid var(--color-border, #d4d4d8)',
          background: 'var(--color-surface-card, #fff)',
          color: 'var(--color-text-primary, #3f3f46)',
          fontSize: '13px',
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        {t('microListing.filter.clearFilter')}
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </PortalLink>
    </div>
  );
}

/** Horizontal pill rail above the grid — placement {@code rail-top}. */
function CategoryPills({ categories, activeId, hrefTemplate, q, tag }: CategoryFilterProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <div {...part('filter')} style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <PortalLink href={withQuery('?', q, tag)} style={pill(!activeId)}>
        {t('microListing.filter.all')}
      </PortalLink>
      {categories.map((c) => {
        const slug = String(c.id);
        const active = String(activeId) === slug;
        return (
          <PortalLink
            key={slug}
            href={withQuery(hrefTemplate.replace('{slug}', encodeURIComponent(slug)), q, tag)}
            style={pill(active)}
          >
            {c.name}
          </PortalLink>
        );
      })}
    </div>
  );
}

/** Vertical list beside the grid — placement {@code sidebar}. Same
 *  links as the rail, so the two never disagree about where a category
 *  leads. */
function CategorySidebar({ categories, activeId, hrefTemplate, q, tag }: CategoryFilterProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <nav
      {...part('filter')}
      aria-label={t('microListing.categories.title')}
      style={{ flex: '1 1 200px', maxWidth: 260 }}
    >
      <div
        style={{
          marginBottom: 8,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: 'var(--font-tracking-widest, 0.08em)',
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary, #94a3b8)',
        }}
      >
        {t('microListing.categories.title')}
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
        <li>
          <PortalLink
            href={withQuery('?', q, tag)}
            style={sideItem(!activeId)}
            aria-current={!activeId ? 'page' : undefined}
          >
            {t('microListing.filter.all')}
          </PortalLink>
        </li>
        {categories.map((c) => {
          const slug = String(c.id);
          const active = String(activeId) === slug;
          return (
            <li key={slug}>
              <PortalLink
                href={withQuery(hrefTemplate.replace('{slug}', encodeURIComponent(slug)), q, tag)}
                style={sideItem(active)}
                aria-current={active ? 'page' : undefined}
              >
                {c.name}
              </PortalLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function pill(active: boolean) {
  return {
    display: 'inline-block',
    padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
    borderRadius: 999,
    fontSize: 'var(--font-size-xs, 12px)',
    fontWeight: 600,
    textDecoration: 'none',
    border:
      '1px solid ' +
      (active ? 'var(--color-brand-500, #3b5bdb)' : 'var(--color-border, rgba(0,0,0,0.12))'),
    background: active ? 'var(--color-brand-500, #3b5bdb)' : 'transparent',
    color: active ? '#fff' : 'var(--color-text-primary, #18181b)',
  };
}

function sideItem(active: boolean) {
  return {
    display: 'block',
    padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
    borderRadius: 'var(--radius-sm, 6px)',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: active ? 600 : 500,
    textDecoration: 'none',
    background: active ? 'var(--color-brand-500, #3b5bdb)' : 'transparent',
    color: active ? '#fff' : 'var(--color-text-primary, #18181b)',
  };
}
